import type { intelligence } from '@cwos/api-client';
import { ANALYTICS_PRESETS } from '../../dashboard/analytics/analyticsRange.js';
import { ChartResult } from './ChartResult.js';
import { KpiResult } from './KpiResult.js';
import { TableResult } from './TableResult.js';
import type { ResultAction } from './resultActions.js';
import { humanizeMetricKey, periodPhrase } from './evidence.js';

/**
 * The PROPOSED dashboard — DRAWN, then offered.
 *
 * WHAT THIS CARD USED TO BE, AND WHY IT WAS WRONG. It drew a grid of tiles
 * reading `Trend` over `Hours worked`: the widget's shape-word above the
 * widget's label, with no figures anywhere, beneath a note that a confirmation
 * was waiting. Ask for "a dashboard showing hours for each day" and you were
 * shown two words and asked to approve them. The owner's verdict — "that is
 * backwards" — is exactly right: a person cannot judge a dashboard they have
 * not seen, and asking them to is not caution, it is a rubber stamp with extra
 * steps.
 *
 * SO THE PREVIEW IS THE DASHBOARD. `dashboard.plan` now executes every widget
 * through the read-only analytics engine under the caller's own scope, and
 * each one arrives as an ordinary result payload — a KPI, a chart, a table.
 * They are handed to the SAME components that draw a real answer
 * ({@link KpiResult}, {@link ChartResult}, {@link TableResult}), so the
 * preview inherits every rule those components already keep: a null is a gap
 * and never a zero, a withheld figure says so in words, a series too sparse to
 * plot honestly renders as its own table instead of a line through one point.
 * A second, preview-only chart engine would be a second place for all of that
 * to be got subtly wrong.
 *
 * NOTHING HAS BEEN SAVED, AND NOTHING NEEDED CONFIRMING. Drawing this cost a
 * read of data the viewer could already ask for one measure at a time. There
 * is no pending confirmation behind this card, because there is no change
 * behind it. Saving is a separate, explicit act — the [Save to Dashboards]
 * button below — and THAT is what raises a confirmation, bound to the exact
 * spec shown here.
 *
 * THE PROPORTIONS ARE THE SPEC'S. The grid mirrors the spec's own width
 * language (`full` / `half` / `third`), so the shape of the preview is the
 * shape of the dashboard that would be saved.
 *
 * F275 / F285 — WHERE A WIDGET'S CAPTION COMES FROM, AND WHY IT IS THE
 * SERVER'S JOB. A caption is needed only where the server sent no figures: a
 * widget with no `render`, or a preview with no `widgetPreviews` at all. Every
 * drawn widget is captioned by the payload's own `label`.
 *
 * This card used to take an optional `metrics` catalog and prefer a metric's
 * catalogued display name over {@link humanizeMetricKey}. NO HOST EVER SUPPLIED
 * IT — `ConversationSurface` is the only production render of `ResultBlocks`
 * and passed nothing — so every caption came from the key regardless, and the
 * prop was a capability the type advertised and nothing provided. It is gone
 * rather than wired: the only catalog a browser can fetch
 * (`GET /api/dashboard/metrics` → `MetricDefinition`) does not carry
 * `supportsSiteFilter` / `supportsProgramFilter`, so satisfying the old
 * `MetricInfo` type meant inventing two capability booleans; and paying an
 * unconditional catalog request on every mount of BOTH hosts of this surface,
 * to improve wording that only appears when the figures failed, is the wrong
 * trade.
 *
 * THE SERVER NOW SENDS THE NAME. `ResultDashboardWidgetPreview.label` carries
 * the catalogue's own display name, set by the preview executor which already
 * had the catalogue open — including on the two paths where the engine could
 * not answer, which are exactly the paths a caption is read on. That matters
 * for the minority of keys where a de-slug reads worst: `care_gaps` is
 * "Clients behind plan", `no_shows` is "Missed shifts",
 * `verification_failures` is "Location verification failures".
 *
 * {@link humanizeMetricKey} survives for the ONE case with no executed widget
 * to take a name from: a preview whose engine answered for nothing, where the
 * only thing that exists is the spec's list of keys. It is honest there —
 * "Care gaps" is a plainer name than the catalogue's, never a wrong one, and
 * never the raw key.
 *
 * FILTERS ARE COUNTED, NOT LISTED. `programIds` and `siteIds` are uuids. How
 * many programs a dashboard is scoped to is a fact a person can check; which
 * uuids they are is a key printed at somebody. The date range IS shown,
 * because dates are the thing they appear to be.
 *
 * Absent payload → renders nothing.
 */
export function DashboardPreviewCard({
  preview,
  onAction,
}: {
  preview: intelligence.ResultDashboardPreview | null | undefined;
  /**
   * Where [Save to Dashboards] and [Edit] go. Absent on a surface that has not
   * wired the conversation yet — in which case the buttons are not drawn at
   * all, because a button that does nothing is worse than no button.
   */
  onAction?: (action: ResultAction) => void;
}) {
  if (preview === null || preview === undefined) return null;

  const spec = preview.validatedSpec;
  const widgetPreviews = preview.widgetPreviews ?? [];

  return (
    <section className="rn-card card-record" aria-label={`Dashboard preview: ${spec.name}`}>
      <div className="rn-card-head">
        <h3 className="rn-card-title">{spec.name}</h3>
        <span className="rn-muted">
          {spec.scope === 'personal' ? 'Personal' : 'Organization'} ·{' '}
          {periodSentence(preview.period, spec.periodPreset)}
          {spec.compare ? ' · compared with the period before' : ''}
        </span>
      </div>

      {widgetPreviews.length > 0 ? (
        <ul
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
            gap: 'var(--space-12)',
            listStyle: 'none',
            margin: 'var(--space-12) 0 0',
            padding: 0,
          }}
        >
          {widgetPreviews.map((widget, index) => (
            <li
              key={`${widget.metricKey}-${index}`}
              style={{ gridColumn: `span ${COLUMN_SPAN[widget.width]}`, minWidth: 0 }}
            >
              {/* The SERVER'S name for the measure, from the catalogue it
                  already had open when it executed this widget. */}
              <WidgetPreview widget={widget} />
            </li>
          ))}
        </ul>
      ) : (
        // No `widgetPreviews` at all means no analytics engine answered for
        // this turn — a different fact from "every measure was empty", and
        // said as the different fact it is. The spec is still described below,
        // so the person knows what was proposed even though it could not be
        // drawn.
        <p className="rn-muted" style={{ margin: 'var(--space-12) 0 0' }} role="status">
          The figures for this dashboard could not be loaded, so it is described here rather than
          drawn.{' '}
          {spec.widgets.length === 1
            ? 'It has one widget: '
            : `It has ${spec.widgets.length} widgets: `}
          {spec.widgets
            .map((widget) =>
              // F286: a widget's breakdown is part of what was PROPOSED, so the
              // description of an undrawn dashboard has to carry it. Naming the
              // measure alone here would describe a different dashboard from the
              // one the person asked for — on the one path where the description
              // is all they get.
              widget.groupBy === undefined
                ? humanizeMetricKey(widget.metricKey)
                : `${humanizeMetricKey(widget.metricKey)} by ${widget.groupBy}`,
            )
            .join(', ')}
          .
        </p>
      )}

      <FilterSummaryLine filters={spec.filters} />

      {preview.reasons.length > 0 ? (
        <ul style={{ margin: 'var(--space-10) 0 0', paddingLeft: 'var(--space-20)' }}>
          {preview.reasons.map((reason) => (
            <li key={reason} className="rn-muted">
              {reason}
            </li>
          ))}
        </ul>
      ) : null}

      {!preview.persistable ? (
        <p className="tc-warn" style={{ marginTop: 'var(--space-10)' }} role="alert">
          This cannot be saved as described. The notes above say what stands in the way.
        </p>
      ) : null}

      {onAction !== undefined ? (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 'var(--space-8)',
            marginTop: 'var(--space-12)',
          }}
        >
          <button
            type="button"
            className="msg-primary"
            disabled={!preview.persistable}
            onClick={() =>
              // Name only: the action is a sentence, and the plan identity
              // on `preview` is the server's own (F290). See resultActions.ts.
              onAction({ kind: 'save_dashboard', name: spec.name })
            }
          >
            Save to Dashboards
          </button>
          {/* F261: THE VISIBLE WORD IS "Edit"; THE SPOKEN NAME SAYS WHAT OF.
              A screen reader user pulling up the controls on this screen heard
              "Edit, button" — a verb with no object, on a surface where an
              answer can carry more than one preview and the page itself has an
              Edit for other things entirely. The `aria-label` names the
              dashboard, in the same `noun: name` form the card's own region
              label and the automation card already use, so the two are heard as
              one vocabulary. It still BEGINS with the visible word, which is
              what keeps a voice-control user's "click Edit" working (WCAG
              2.5.3): an accessible name that dropped the label the eye can see
              would fix the audit line and break the speaking user.

              [Save to Dashboards] is deliberately left as it is. Its name
              already carries a verb and a destination, which is the thing that
              was missing here. */}
          <button
            type="button"
            className="rn-btn-quiet"
            aria-label={`Edit dashboard: ${spec.name}`}
            onClick={() => onAction({ kind: 'edit_dashboard', name: spec.name })}
          >
            Edit
          </button>
        </div>
      ) : null}

      <p className="rn-muted" style={{ margin: 'var(--space-10) 0 0' }}>
        Nothing has been saved. This is a view of your own data — looking at it changes nothing.
      </p>
    </section>
  );
}

/**
 * One executed widget.
 *
 * The `render` payload IS an ordinary result object, so it goes straight to
 * the component that owns that kind. A widget with no render says why in the
 * words the server sent — never a zero, never an empty chart frame.
 */
function WidgetPreview({ widget }: { widget: intelligence.ResultDashboardWidgetPreview }) {
  const render = widget.render;
  if (render === undefined) {
    return (
      <div
        style={{
          border: '1px solid var(--color-line)',
          borderRadius: 'var(--radius-sm)',
          background: 'var(--color-sunken)',
          padding: 'var(--space-12)',
          height: '100%',
        }}
      >
        <p style={{ margin: 0, fontWeight: 600 }}>{widget.label}</p>
        <p className="rn-muted" style={{ margin: 'var(--space-4) 0 0' }}>
          {widget.unavailable ?? 'No figures were returned for this measure.'}
        </p>
        <SavedDifference note={widget.savedDifference} />
      </div>
    );
  }

  return (
    <>
      {render.kind === 'kpi' ? <KpiResult kpi={render} /> : null}
      {render.kind === 'chart' ? <ChartResult chart={render} /> : null}
      {render.kind === 'table' ? <TableResult table={render} /> : null}
      <SavedDifference note={widget.savedDifference} />
    </>
  );
}

/**
 * WHAT THE SAVED DASHBOARD WILL DRAW INSTEAD (F308).
 *
 * Rendered UNDER the figures rather than in place of them, and on the
 * unavailable tile as well, because the two facts are independent: a widget can
 * have perfectly good numbers above a picture that is not the one being kept.
 *
 * It is deliberately not styled as an error. Nothing has gone wrong — the
 * preview is honest and the saved widget will be honest; they are simply drawn
 * by two different machines, and the person about to approve one is entitled to
 * know which one they are looking at.
 */
function SavedDifference({ note }: { note?: string }) {
  if (note === undefined) return null;
  return (
    <p
      className="rn-muted"
      style={{ margin: 'var(--space-6) 0 0', fontSize: 'var(--text-xs, 0.8125rem)' }}
    >
      {note}
    </p>
  );
}

/**
 * What the dashboard is scoped to — as counts for the id-bearing dimensions and
 * as dates for the range, which is the only one that is what it looks like.
 */
function FilterSummaryLine({ filters }: { filters: intelligence.DashboardSpecFilters }) {
  const parts: string[] = [];
  const programs = filters.programIds?.length ?? 0;
  const sites = filters.siteIds?.length ?? 0;
  if (programs > 0) parts.push(`${programs} ${programs === 1 ? 'program' : 'programs'}`);
  if (sites > 0) parts.push(`${sites} ${sites === 1 ? 'site' : 'sites'}`);
  if (filters.from !== undefined && filters.to !== undefined) {
    parts.push(`${filters.from} to ${filters.to}`);
  }
  if (parts.length === 0) {
    return (
      <p className="rn-muted" style={{ margin: 'var(--space-10) 0 0' }}>
        No filters — everything your account can see.
      </p>
    );
  }
  return (
    <p className="rn-muted" style={{ margin: 'var(--space-10) 0 0' }}>
      Scoped to {parts.join(', ')}.
    </p>
  );
}

/** The spec's width language as grid spans over a six-column preview. */
const COLUMN_SPAN: Readonly<Record<intelligence.DashboardWidgetSpec['width'], number>> = {
  full: 6,
  half: 3,
  third: 2,
};

/**
 * The period, named by the boundaries the figures actually came from.
 *
 * The RESOLVED window wins whenever the server sent one: "this week" resolves
 * against the organisation's own calendar and week start, and a caption that
 * said "This week" over figures drawn from a window the viewer cannot see is
 * a caption they cannot check. The preset label is the fallback for a preview
 * that produced no figures to have boundaries for.
 */
function periodSentence(
  period: { from: string; to: string } | undefined,
  preset: intelligence.DashboardSpec['periodPreset'],
): string {
  if (period === undefined) return presetLabel(preset);
  return periodPhrase(period, undefined) ?? `${period.from} to ${period.to}`;
}

/** Preset labels, from the analytics workspace's own list — one vocabulary. */
function presetLabel(preset: intelligence.DashboardSpec['periodPreset']): string {
  return ANALYTICS_PRESETS.find((entry) => entry.value === preset)?.label ?? 'This week';
}
